import { useCallback, useEffect, useMemo } from 'react';
import { createHookContext } from '@clover-util/react-context';
import { useStorageExpiry } from 'hooks/useStorageExpiry';
import { useLocalStorageState } from '@clover-util/use-storage-state';
import { scheduledOrderKey } from './ScheduledOrderMethods.util';
import { useMerchant } from './merchant';
import { cartExpiryKey } from './cart/util';
import { DateTime } from 'luxon';

type ScheduledOrderDateTime = {
  currentDate?: number;
  currentTime?: number;
}

export const { useScheduledOrderMethods, ScheduledOrderMethodsProvider } = createHookContext('ScheduledOrderMethods', () => {
  const { merchant } = useMerchant();
  const merchantId = merchant?.merchantUuid;
  const timezone = merchant?.timezone || 'local';
  const enabled = merchant?.scheduledOrdering?.enabled;

  const [scheduledOrder, updateScheduledOrder] = useLocalStorageState<ScheduledOrderDateTime>(merchantId && scheduledOrderKey(merchantId), {});
  const [isExpired, updateExpiry] = useStorageExpiry(cartExpiryKey);

  // Clear Storage when expired
  useEffect(() => {
    if (isExpired) updateScheduledOrder({ });
  }, [isExpired, updateScheduledOrder, scheduledOrder]);

  // Update Expiry everytime time changes
  useEffect(updateExpiry, [scheduledOrder, updateExpiry]);

  // If scheduling is disabled, clear local storage
  useEffect(() => {
    if (!enabled) updateScheduledOrder({ });
  }, [enabled, updateScheduledOrder]);

  return {
    updateDate: useCallback((date: number) => {
      updateScheduledOrder({ currentDate: date });
    }, [updateScheduledOrder]),
    updateTime: useCallback((time: number) => {
      updateScheduledOrder(state => ({ ...state, currentTime: time }));
    }, [updateScheduledOrder]),
    clearScheduledOrder: useCallback(() => {
      updateScheduledOrder({ });
    }, [updateScheduledOrder]),
    scheduledOrder: useMemo(() => scheduledOrder, [scheduledOrder]),
    scheduledTime: useMemo(() => {
      if (!scheduledOrder.currentDate || !scheduledOrder.currentTime) return;
      const time = DateTime.fromMillis(scheduledOrder.currentTime).setZone(timezone);
      const date = DateTime.fromMillis(scheduledOrder.currentDate).setZone(timezone);

      return DateTime.fromObject({
        hour: time.hour,
        minute: time.minute,
        month: date.month,
        day: date.day,
        year: date.year
      }, { zone: timezone }).toMillis();
    }, [scheduledOrder.currentDate, scheduledOrder.currentTime, timezone]),
    updateScheduledOrder
  };
});
